import React, { useEffect, useMemo, useState } from 'react';
import {
    BooleanInput,
    CheckboxGroupInput,
    Create,
    Datagrid,
    FunctionField,
    List,
    NumberInput,
    RadioButtonGroupInput,
    SelectInput,
    SimpleForm,
    TextField,
    TextInput,
    maxValue,
    minValue,
    number,
    required,
    useGetList,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { Alert, Box, Chip, Typography } from '@mui/material';
import { FormToolbar } from '../../common/FormToolbar';
import { fromLocalInput, schoolApi, showTime, toLocalInput } from '../../common/schoolApi';
import { AssignmentShow } from './AssignmentShow';
import { RESULT_MODES, WINDOW_COLOUR } from './constants';

/**
 * Assignments -- setting a test for a class. Phase 4, task 4.8, over the API
 * of tasks 4.4 and 4.7.
 *
 * Who gets it (decided 2026-09-11, option 2): the active students of the
 * chosen sections at the moment it is published. The form says so, because
 * a teacher will otherwise expect a student added next week to get it too.
 *
 * Times are entered and shown in the browser's zone and sent as ISO UTC
 * (common/schoolApi). The API compares them only with the database's clock.
 */

// =========================================================================
// List
// =========================================================================

const filters = [
    <TextInput source="q" label="Search" alwaysOn resettable />,
    <BooleanInput source="mine" label="Only mine" alwaysOn />,
    <SelectInput
        source="window"
        label="When"
        alwaysOn
        choices={[
            { id: 'upcoming', name: 'Upcoming' },
            { id: 'open', name: 'Open now' },
            { id: 'closed', name: 'Closed' },
        ]}
    />,
    <SelectInput
        source="status"
        label="Status"
        choices={[
            { id: 'draft', name: 'Draft' },
            { id: 'published', name: 'Published' },
            { id: 'cancelled', name: 'Cancelled' },
        ]}
    />,
];

const AssignmentList = (props: any) => (
    <List
        title="Assignments"
        {...props}
        filters={filters}
        exporter={false}
        empty={
            <Alert severity="info" sx={{ m: 3 }}>
                Nothing assigned yet. Open a ready test and press &ldquo;Assign to a class&rdquo;, or create an assignment here.
            </Alert>
        }
    >
        <Datagrid rowClick="show" bulkActionButtons={false}>
            <TextField source="title" label="Test" sortable={false} />
            <FunctionField
                label="Type"
                sortable={false}
                render={(r: any) => <Chip size="small" variant="outlined" label={r.quiz_source === 'school' ? 'School test' : 'WiZi mock'} />}
            />
            <FunctionField label="Sections" sortable={false} render={(r: any) => (r.sections || []).map((s: any) => s.label).join(', ')} />
            <FunctionField label="Opens" sortable={false} render={(r: any) => showTime(r.scheduled_start)} />
            <FunctionField label="Closes" sortable={false} render={(r: any) => showTime(r.scheduled_end)} />
            <FunctionField
                label="Status"
                sortable={false}
                render={(r: any) => <Chip size="small" color={WINDOW_COLOUR[r.window_state]} label={r.window_state} />}
            />
            <FunctionField
                label="Submitted"
                sortable={false}
                render={(r: any) =>
                    r.counts ? `${(r.counts.submitted || 0) + (r.counts.auto_submitted || 0)} / ${r.counts.total}` : '—'
                }
            />
            <FunctionField label="By" sortable={false} render={(r: any) => (r.is_mine ? 'Me' : '')} />
        </Datagrid>
    </List>
);

// =========================================================================
// Create
// =========================================================================

type CatalogueItem = { quiz_source: string; quiz_id: number; title: string; time_limit: number; question_count: number; total_marks: number };

const useCatalogue = () => {
    const [items, setItems] = useState<CatalogueItem[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        schoolApi
            .get('assignment/catalogue')
            .then(r => setItems([...r.result.tests, ...r.result.mocks]))
            .catch(e => setError(e.message));
    }, []);
    return { items, error };
};

/** When the test changes, offer its own time limit as the duration. */
const DurationFromTest = ({ catalogue }: { catalogue: CatalogueItem[] }) => {
    const quiz = useWatch({ name: 'quiz' });
    const { setValue, getValues } = useFormContext();
    const item = catalogue.find(c => `${c.quiz_source}:${c.quiz_id}` === quiz);
    useEffect(() => {
        if (item && !getValues('duration_minutes')) setValue('duration_minutes', item.time_limit);
    }, [quiz]);
    if (!item) return null;
    return (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {item.question_count} questions, {item.total_marks} marks, {item.time_limit} minutes as written.
        </Typography>
    );
};

const nextHour = (addHours = 0) => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 1 + addHours);
    return toLocalInput(d.toISOString());
};

const transform = (data: any) => {
    const [quiz_source, quiz_id] = String(data.quiz || '').split(':');
    return {
        quiz_source,
        quiz_id: Number(quiz_id),
        section_ids: data.section_ids || [],
        scheduled_start: fromLocalInput(data.scheduled_start),
        scheduled_end: fromLocalInput(data.scheduled_end),
        duration_minutes: data.duration_minutes || null,
        // Empty means "any time before the end"; the API wants null for that.
        late_start_minutes: data.late_start_minutes === '' || data.late_start_minutes === null || data.late_start_minutes === undefined
            ? null
            : data.late_start_minutes,
        grace_seconds: data.grace_seconds,
        max_attempts: data.max_attempts,
        show_results_mode: data.show_results_mode,
        instructions: data.instructions || null,
        publish: data.publish === 'now',
    };
};

const AssignmentCreate = (props: any) => {
    const { items, error } = useCatalogue();
    const { search } = useLocation();
    const preset = new URLSearchParams(search).get('quiz') || '';
    const { data: sections = [] } = useGetList('class-section', {
        pagination: { page: 1, perPage: 200 },
        sort: { field: 'class_name', order: 'ASC' },
    });

    const choices = useMemo(
        () =>
            (items || []).map(c => ({
                id: `${c.quiz_source}:${c.quiz_id}`,
                name: `${c.quiz_source === 'school' ? 'Test' : 'WiZi mock'}: ${c.title} (${c.question_count} Q, ${c.time_limit} min)`,
            })),
        [items]
    );

    if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
    if (items === null) return <Typography sx={{ m: 3 }}>Loading…</Typography>;

    return (
        <Create {...props} title="Assign a test" transform={transform} redirect="show">
            <SimpleForm
                toolbar={<FormToolbar {...props} />}
                defaultValues={{
                    quiz: preset,
                    scheduled_start: nextHour(),
                    scheduled_end: nextHour(2),
                    late_start_minutes: 15,
                    grace_seconds: 60,
                    max_attempts: 1,
                    show_results_mode: 'after_window',
                    publish: 'now',
                }}
            >
                {choices.length === 0 && (
                    <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
                        You have no ready tests and there are no published WiZi mocks. Build a test and mark it ready first.
                    </Alert>
                )}
                <SelectInput source="quiz" label="Test" choices={choices} validate={required()} fullWidth />
                <DurationFromTest catalogue={items} />

                <CheckboxGroupInput
                    source="section_ids"
                    label="Sections"
                    choices={sections.map((s: any) => ({ id: s.id, name: `${s.label}${s.student_count !== undefined ? ` (${s.student_count})` : ''}` }))}
                    validate={required('Choose at least one section.')}
                    helperText="Only the sections you teach are listed. Students in them when you publish get the test; students added later do not."
                />

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: '100%' }}>
                    <TextInput source="scheduled_start" label="Opens" type="datetime-local" validate={required()} InputLabelProps={{ shrink: true }} />
                    <TextInput source="scheduled_end" label="Closes" type="datetime-local" validate={required()} InputLabelProps={{ shrink: true }} />
                    <NumberInput
                        source="duration_minutes"
                        label="Time for each student (min)"
                        validate={[number(), minValue(1), maxValue(600)]}
                        helperText="Counted from when they press Start"
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: '100%' }}>
                    <NumberInput
                        source="late_start_minutes"
                        label="Can start up to (min) late"
                        validate={[number(), minValue(0), maxValue(600)]}
                        helperText="Empty = any time before it closes"
                    />
                    <NumberInput source="grace_seconds" label="Grace (seconds)" validate={[number(), minValue(0), maxValue(600)]} helperText="For slow networks" />
                    <NumberInput source="max_attempts" label="Attempts" validate={[number(), minValue(1), maxValue(10)]} helperText="The best score counts" />
                </Box>
                <SelectInput source="show_results_mode" label="Students see results" choices={RESULT_MODES} validate={required()} />
                <TextInput source="instructions" label="Instructions for students" multiline minRows={2} fullWidth />
                <RadioButtonGroupInput
                    source="publish"
                    label=""
                    choices={[
                        { id: 'now', name: 'Publish now' },
                        { id: 'draft', name: 'Save as a draft' },
                    ]}
                />
            </SimpleForm>
        </Create>
    );
};

export default { list: AssignmentList, create: AssignmentCreate, show: AssignmentShow };
